import {
  ArrowRight,
  BadgeCheck,
  Banknote,
  BookOpen,
  BriefcaseBusiness,
  Building2,
  Check,
  ChevronRight,
  CircleHelp,
  ClipboardCheck,
  Clock3,
  FileCheck2,
  FileText,
  Gavel,
  Headphones,
  HeartHandshake,
  Landmark,
  MapPin,
  PackageCheck,
  Phone,
  Scale,
  ShieldAlert,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { createElement, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const imageUrls = {
  overview: "https://res.cloudinary.com/nemfcgrj/image/upload/v1791189969/suraksha24x7_term_and_conditions_1.png",
  overview2: "https://res.cloudinary.com/nemfcgrj/image/upload/v1791195005/211aeebc-acac-42f2-8940-36c60c9890ba.png",
  siteVisit: "https://res.cloudinary.com/nemfcgrj/image/upload/v1791193020/suraksha24x7_terms_and_conditions_3.png",
  payment: "https://res.cloudinary.com/nemfcgrj/image/upload/v1791193233/suraksha24x7_terms_and_conditions_4.png",
  installation: "https://res.cloudinary.com/nemfcgrj/image/upload/v1791196055/suraksha24x7_terms_and_conditions.png",
  warranty: "https://res.cloudinary.com/nemfcgrj/image/upload/v1791196238/suraksha24x7_terms_and_conditions5.png",
  warranty2: "https://res.cloudinary.com/nemfcgrj/image/upload/v1791196528/suraksha24x7_terms_and_conditions_6.png",
  security: "https://res.cloudinary.com/nemfcgrj/image/upload/v1791196773/suraksha24x7_terms_and_conditions_7.png",
};

const scopeItems = [
  "Enquiries",
  "Quotations",
  "Site Visits",
  "Purchases",
  "Projects",
  "Installations",
  "Maintenance",
  "Technical Support",
  "Service Requests",
];

const sections = [
  {
    id: "about-suraksha",
    title: "About Suraksha 24X7",
    icon: Building2,
    image: imageUrls.overview2,
    imageAlt: "Suraksha 24X7 terms and conditions",
    clauses: [
      {
        title: "Security, IT and technology solutions",
        paragraphs: [
          "Suraksha 24X7 provides security surveillance, electronic security, IT products, networking and technology solutions for residential, commercial, institutional and industrial customers.",
          "Our solutions may include CCTV surveillance, access control, biometric attendance, video door phones, intrusion alarms, networking, servers, storage, structured cabling, Wi-Fi, firewalls, UPS systems, IP telephony, public-address systems, home automation, solar solutions and other security and IT products and services.",
          "The exact scope of every project is determined by the customer's approved quotation, proposal, bill of quantities, work order or service agreement.",
        ],
      },
    ],
  },
  {
    id: "customer-commitment",
    title: "Our Commitment to Customers",
    icon: HeartHandshake,
    lead: "Suraksha 24X7 aims to provide professional, transparent and dependable service throughout the customer relationship. We are committed to:",
    bullets: [
      "Genuine and appropriately sourced products wherever specified in the quotation.",
      "Professional consultation and solution planning.",
      "Clear quotations and defined scopes of work.",
      "Qualified installation and configuration according to the agreed project requirements.",
      "Transparent pricing without intentionally concealed charges.",
      "Fast and responsible service coordination.",
      "After-sales assistance for products and services supplied by us.",
      "Respect for customer property, privacy and business operations.",
      "Clear communication regarding warranties, exclusions and additional work.",
      "Fair handling of complaints and service requests.",
    ],
  },
  {
    id: "site-visit",
    title: "First Preliminary Site Visit – Free",
    icon: MapPin,
    image: imageUrls.siteVisit,
    imageAlt: "Customer consultation and site visit",
    imageOnRight: true,
    highlight:
      "For eligible customers and projects within our standard service area, the first preliminary site visit may be provided free of professional consultation charges.",
    clauses: [
      {
        title: "Purpose of the first visit",
        paragraphs: [
          "The purpose of the first visit is to understand the customer's requirements, inspect reasonably accessible areas and recommend an appropriate solution.",
        ],
      },
      {
        title: "Special requirements",
        paragraphs: [
          "A free site visit does not automatically include specialized testing, civil work, high-access inspection, boom lift/scaffolding, travel outside the normal service area, paid parking/toll expenses, detailed engineering drawings, third-party technical audits or extensive surveying.",
          "If any such charge is required, the customer will be informed before the chargeable work is undertaken. A free site visit does not obligate either party to proceed with the project.",
        ],
      },
    ],
  },
  {
    id: "support",
    title: "24×7 Support Request Availability",
    icon: Headphones,
    highlight:
      "Suraksha 24X7 accepts customer support and service requests on a 24×7 basis through designated support channels, subject to network/system availability.",
    callout:
      "24×7 support availability means that customers may submit or report eligible service issues at any time. It does not automatically guarantee 24×7 physical engineer attendance or immediate resolution.",
    clauses: [
      {
        title: "Factors affecting response and attendance",
        bullets: [
          "Severity of the issue",
          "Customer location",
          "Engineer availability",
          "Spare-part availability",
          "Product/manufacturer support",
          "Site accessibility",
          "Working and safety conditions",
          "Public holidays and local restrictions",
          "Weather, traffic and other circumstances beyond reasonable control",
          "Applicable AMC/SLA or project agreement",
        ],
      },
      {
        title: "Applicable service commitments",
        paragraphs: [
          "Where a separate Service Level Agreement or AMC specifies response or resolution times, that agreement will take priority.",
        ],
      },
    ],
  },
  {
    id: "quotation",
    title: "Quotation & Project Terms",
    icon: FileCheck2,
    clauses: [
      {
        title: "Quotations",
        paragraphs: [
          "Unless otherwise stated in writing, quotations are subject to product availability, taxes, technical feasibility and the validity period specified on the quotation.",
          "Prices may change after quotation expiry due to changes in manufacturer pricing, taxes, foreign exchange, transportation costs, product availability or specifications.",
          "Once a quotation is accepted and payment is received, Suraksha 24X7 will make reasonable efforts to supply the specified products. If a product becomes unavailable, the customer will be informed before a materially different substitute is supplied.",
        ],
      },
      {
        title: "Scope of work",
        paragraphs: [
          "Only products, quantities, installation activities and services expressly mentioned in the accepted quotation/work order are included in the project price.",
          "Unless specifically included, additional requirements such as civil work, electrical work, dedicated power points, internet connectivity, trenching, concealed cabling, scaffolding, lifts, core cutting, painting, furniture modification, additional network infrastructure or third-party integration may be charged separately.",
          "No material additional charge will be imposed without informing or obtaining approval from the customer or their authorized representative.",
        ],
      },
      {
        title: "Customer responsibilities",
        paragraphs: [
          "To allow safe and timely execution, the customer is responsible for providing safe and reasonable access to the installation location; necessary electricity and network connectivity where applicable; required permissions from the property owner, society, management or authorities; accurate site and technical information; and an authorized person for approvals and project coordination.",
          "The customer must inform Suraksha 24X7 of known concealed electrical cables, water pipes, gas lines, structural restrictions or other hazards before drilling or installation.",
          "Delays caused by unavailable access, unfinished civil/electrical work, third-party contractors or customer-requested changes may affect the project schedule and cost.",
        ],
      },
      {
        title: "Project changes and approvals",
        paragraphs: [
          "Any change requested after quotation approval, including changes in quantity, product model, installation location, cabling route, configuration, integration, or project scope, may affect the project cost and completion timeline.",
          "Where such changes require additional material, labour, travel, configuration, or third-party coordination, Suraksha 24X7 may issue a revised quotation or change confirmation before proceeding.",
          "Work outside the originally approved scope will be undertaken only after reasonable communication and approval from the customer or their authorized representative.",
        ],
      },
    ],
  },
  {
    id: "payment",
    title: "Payment Terms & Conditions",
    icon: Banknote,
    image: imageUrls.payment,
    imageAlt: "Business payment and invoicing",
    clauses: [
      {
        title: "Payment structure",
        paragraphs: [
          "The payment schedule will primarily be governed by the accepted quotation, purchase order or project agreement.",
          "Where no separate payment schedule is specified, Suraksha 24X7 may require an advance before procurement or commencement of work, a progress payment during supply/installation and the outstanding balance upon completion, commissioning or handover.",
          "For specially ordered, customized, imported or project-specific equipment, a higher advance or full advance may be required.",
        ],
      },
      {
        title: "Advance payment",
        paragraphs: [
          "Advance payment confirms the customer's intention to proceed and enables procurement, scheduling and allocation of resources.",
          "Once specially ordered/customized goods have been procured specifically for the customer, cancellation or refund may be restricted to the extent reasonably attributable to committed procurement or other non-recoverable costs, subject to applicable law. Any applicable cancellation deduction will be communicated transparently.",
        ],
      },
      {
        title: "Taxes and invoices",
        paragraphs: [
          "GST and other applicable taxes will be charged according to prevailing law and the quotation/invoice.",
          "The customer should provide correct billing information and GST details, where applicable, before invoice generation. Any statutory deduction such as TDS must be handled according to applicable law, and the customer should provide the appropriate certificate/documentation within the prescribed period.",
        ],
      },
      {
        title: "Payment methods",
        paragraphs: [
          "Payments should be made only through payment methods officially communicated or authorized by Suraksha 24X7. Customers should verify payment details before transferring funds. Suraksha 24X7 will not intentionally request payment through an unauthorized personal account or unapproved payment channel.",
        ],
      },
      {
        title: "Payment delays",
        paragraphs: [
          "Payments must be made according to the agreed schedule. Where an undisputed payment becomes overdue, Suraksha 24X7 may, after reasonable communication, suspend further non-emergency work, delivery or chargeable support until the outstanding amount is cleared, subject to the applicable contract and law.",
          "Such suspension does not remove either party's obligations regarding products or services already paid for or statutory consumer rights.",
        ],
      },
      {
        title: "Ownership of goods",
        paragraphs: [
          "To the extent permitted by applicable law and the agreed contract, ownership/title to supplied goods may remain with the seller until full payment relating to those goods has been received.",
          "Risk, custody and responsibility for installed equipment will be governed by the applicable quotation, invoice or project agreement.",
        ],
      },
    ],
  },
  {
    id: "installation",
    title: "Installation & Handover",
    icon: Wrench,
    image: imageUrls.installation,
    imageAlt: "Installation and project support",
    imageOnRight: true,
    clauses: [
      {
        title: "Installation and customer inspection",
        paragraphs: [
          "Installation will be performed according to the agreed scope and reasonably practicable site conditions.",
          "The customer or authorized representative should inspect the installation during handover and promptly report visible deficiencies.",
          "Any additional requirement requested during installation may require a revised quotation or change order.",
        ],
      },
      {
        title: "Project timelines",
        paragraphs: [
          "Estimated completion dates are provided in good faith but may depend on product availability, site readiness, customer approvals, transportation, manufacturer dependencies and third-party infrastructure.",
          "Suraksha 24X7 will make reasonable efforts to communicate material delays.",
          "A delay caused by circumstances reasonably beyond the company's control will not automatically constitute service deficiency where the company has acted reasonably and applicable law provides otherwise.",
        ],
      },
    ],
  },
  {
    id: "warranty",
    title: "Products, Warranty & After-Sales Service",
    icon: BadgeCheck,
    image: imageUrls.warranty,
    imageAlt: "Warranty and after-sales service",
    callout:
      "Product warranty and on-site service coverage are not necessarily the same.",
    clauses: [
      {
        title: "Product authenticity",
        paragraphs: [
          "Suraksha 24X7 aims to supply products corresponding to the brand, model and specifications stated in the final quotation or invoice.",
          "Product packaging, appearance, firmware, accessories and minor specifications may occasionally change because of manufacturer revisions. A materially different substitute will not knowingly be supplied as the originally quoted product without customer communication/approval.",
        ],
      },
      {
        title: "Warranty",
        paragraphs: [
          "Product warranty is normally governed by the respective manufacturer/OEM warranty policy unless Suraksha 24X7 expressly provides a separate written warranty.",
          "Warranty duration may differ between cameras, recorders, hard drives, networking products, access-control equipment, computers, UPS systems, batteries and other products. The applicable warranty should therefore be determined from the invoice, warranty documentation and manufacturer's terms rather than assumed from another product.",
        ],
      },
      {
        title: "Warranty exclusions",
        paragraphs: [
          "Subject to applicable law and manufacturer policy, warranty may not cover damage caused by physical damage; misuse or negligence; unauthorized repair/modification; voltage fluctuation or electrical surge; lightning; water/fire damage; pests/rodents; accidental damage; incorrect third-party installation; incompatible third-party equipment; environmental conditions outside product specifications; or circumstances classified as force majeure.",
          "Consumable items and batteries may carry separate warranty conditions. These exclusions do not override rights that cannot lawfully be excluded.",
        ],
      },
      {
        title: "Warranty service",
        paragraphs: [
          "Where a covered product requires manufacturer warranty processing, Suraksha 24X7 may assist with coordination where applicable.",
          "Replacement or repair time may depend upon diagnosis, manufacturer authorization, service-center processing and spare-part availability. An immediate replacement cannot be guaranteed unless specifically provided under the applicable warranty/SLA.",
        ],
      },
    ],
  },
  {
    id: "service-amc",
    title: "Service & Maintenance",
    icon: Clock3,
    image: imageUrls.warranty2,
    imageAlt: "Service and maintenance support",
    clauses: [
      {
        title: "Free warranty vs chargeable service",
        paragraphs: [
          "A product warranty and an on-site service contract are not necessarily the same. Manufacturer warranty may cover repair/replacement of eligible defective hardware while engineer visits, transportation, reinstallation, configuration, consumables or other labour may be chargeable unless included in the customer's project warranty, AMC or SLA.",
          "Applicable charges will be communicated before chargeable service is undertaken.",
        ],
      },
      {
        title: "AMC – Annual Maintenance Contract",
        paragraphs: [
          "Customers may purchase an Annual Maintenance Contract where available. AMC coverage, preventive visits, response commitments, labour, spare parts and exclusions will be defined in the applicable AMC agreement.",
          "An AMC does not automatically include replacement equipment unless specifically stated.",
        ],
      },
    ],
  },
  {
    id: "cancellation",
    title: "Cancellation, Return & Refund",
    icon: PackageCheck,
    clauses: [
      {
        title: "Order cancellation",
        paragraphs: [
          "Cancellation requests should be made through an official Suraksha 24X7 communication channel.",
          "Eligibility for cancellation depends on the order stage, whether products have already been procured/dispatched/installed, whether products were customized or specially ordered, and the applicable quotation or contract.",
          "Any lawful refund due will be processed after applicable deductions, if any, are clearly determined.",
        ],
      },
      {
        title: "Defective or incorrect products",
        paragraphs: [
          "If a customer receives an incorrect, materially different or defective product, the customer should notify Suraksha 24X7 promptly with the invoice/order details and reasonable supporting information.",
          "The matter will be evaluated according to applicable law, product condition, manufacturer policy and contractual terms.",
          "Nothing in these Terms is intended to remove any statutory remedy available to a consumer for defective, deficient, spurious or misrepresented goods/services.",
        ],
      },
    ],
  },
  {
    id: "security-responsibility",
    title: "Security System Limitations",
    icon: ShieldAlert,
    image: imageUrls.security,
    imageAlt: "Customer protection and system security",
    highlight:
      "No electronic security system can guarantee the prevention of theft, intrusion, cyberattack, accident, fire, vandalism, loss or other incident.",
    clauses: [
      {
        title: "System performance",
        paragraphs: [
          "CCTV, access control, alarms, cybersecurity, networking and related systems are designed to assist with monitoring, detection, recording, access management and risk reduction.",
          "System performance may depend upon electricity, internet connectivity, network infrastructure, storage, user configuration, maintenance and third-party platforms.",
          "Suraksha 24X7 does not represent security technology as a substitute for reasonable physical security practices, insurance or emergency services.",
        ],
      },
      {
        title: "CCTV and privacy responsibility",
        paragraphs: [
          "Customers are responsible for operating cameras, biometric systems, recording equipment and other monitoring technology lawfully at their premises.",
          "The customer is responsible for obtaining necessary permissions, notices and consents and for determining where surveillance is legally appropriate.",
          "Suraksha 24X7 may provide technical installation guidance but does not assume responsibility for unlawful use of equipment by a customer after installation.",
        ],
      },
      {
        title: "Passwords and cybersecurity",
        paragraphs: [
          "Customers should change default passwords and maintain secure credentials for cameras, NVRs, DVRs, routers, servers, cloud accounts and other connected systems. Passwords and administrative credentials should not be shared unnecessarily.",
          "Suraksha 24X7 is not responsible for unauthorized access resulting from customer-controlled credentials, insecure third-party networks or changes made after handover, except where loss is directly attributable to Suraksha 24X7's own acts or omissions as determined under applicable law.",
        ],
      },
      {
        title: "Maintenance and customer responsibility",
        paragraphs: [
          "Security and IT systems require periodic maintenance, testing, software or firmware updates, storage checks and inspection of connected devices to remain effective.",
          "Customers should promptly report faults, damaged equipment, recording failures, connectivity issues or unusual system behaviour. Suraksha 24X7 cannot guarantee continued system performance where recommended maintenance is ignored, equipment is altered by unauthorized persons, or faults remain unreported for an extended period.",
        ],
      },
    ],
  },
  {
    id: "customer-rights",
    title: "Fair Terms for Customers and Company",
    icon: Scale,
    columns: [
      {
        title: "Customer rights",
        icon: ShieldCheck,
        clauses: [
          {
            title: "Right to information",
            paragraphs: [
              "Before making a significant purchase, customers may request reasonable clarification regarding product specifications, pricing, taxes, installation charges, warranty, payment schedule, project scope and expected service coverage.",
              "Suraksha 24X7 encourages customers to review the quotation carefully before approval.",
            ],
          },
          {
            title: "Right to fair service",
            paragraphs: [
              "Customers are entitled to receive the goods and services agreed in the accepted quotation/order, subject to the agreed specifications, scope and applicable law.",
              "Where a genuine service deficiency is reported, Suraksha 24X7 will make reasonable efforts to investigate and provide an appropriate remedy.",
            ],
          },
        ],
      },
      {
        title: "Company rights and agreed changes",
        icon: BriefcaseBusiness,
        clauses: [
          {
            title: "Right to fair payment",
            paragraphs: [
              "Suraksha 24X7 is entitled to receive payment for products supplied and services properly performed according to the accepted quotation or agreement.",
              "A customer should not unreasonably withhold an undisputed payment for completed work because of a separate unrelated matter. Any disputed amount should be communicated with reasonable details so both parties can attempt resolution.",
            ],
          },
          {
            title: "Change requests",
            paragraphs: [
              "Any customer-requested change to product quantity, specifications, camera positions, cabling, networking, software integration or project scope after approval may result in additional charges and/or timeline changes.",
              "The revised scope should be approved before material additional work begins.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "responsibility",
    title: "Responsibility & Force Majeure",
    icon: Gavel,
    clauses: [
      {
        title: "Responsibility and limitation",
        paragraphs: [
          "Each party remains responsible for its own acts and obligations.",
          "To the maximum extent permitted by applicable law, Suraksha 24X7 will not be responsible for indirect or consequential business losses resulting from circumstances beyond its reasonable control or from failures attributable to third-party internet, power, cloud, telecom, manufacturer or customer-controlled systems.",
          "Nothing in these Terms excludes or limits liability that cannot legally be excluded or limited under applicable Indian law.",
        ],
      },
      {
        title: "Force majeure",
        paragraphs: [
          "Neither party will be treated as having breached an obligation to the extent performance is prevented or materially delayed by circumstances beyond reasonable control, including natural disasters, severe weather, fire, epidemic/pandemic restrictions, war, civil disturbance, government restrictions, transport disruption, widespread network/power failures or manufacturer/supply-chain disruption.",
          "The affected party should make reasonable efforts to minimize the impact and resume performance.",
        ],
      },
    ],
  },
  {
    id: "complaints",
    title: "Customer Support & Complaint Resolution",
    icon: CircleHelp,
    clauses: [
      {
        title: "Customer support",
        paragraphs: [
          "Customers experiencing a problem should contact Suraksha 24X7 through the official contact or complaint channels provided on the website or relevant invoice/service document.",
          "Please provide the customer name, registered phone/email, invoice or service reference number, site location, product/system details and a clear description of the issue. This helps the support team identify and resolve the matter efficiently.",
        ],
      },
      {
        title: "Complaint resolution",
        paragraphs: [
          "Suraksha 24X7 aims to handle complaints professionally and fairly.",
          "Depending on the issue, resolution may involve remote troubleshooting, an engineer visit, configuration correction, manufacturer warranty coordination, repair, replacement or another remedy appropriate under the contract and applicable law.",
          "Customers are expected to provide reasonable access and cooperation required for diagnosis.",
        ],
      },
    ],
  },
  {
    id: "website-terms",
    title: "Website Terms",
    icon: BookOpen,
    clauses: [
      {
        title: "Website information",
        paragraphs: [
          "Suraksha 24X7 makes reasonable efforts to maintain accurate website information. Product photographs may be illustrative, and manufacturers may update specifications or designs without prior notice.",
          "Final commercial terms, quantities, specifications and prices are those stated in the accepted quotation/order/invoice rather than general promotional website content where differences exist and have been transparently disclosed.",
        ],
      },
      {
        title: "Intellectual property",
        paragraphs: [
          "Unless otherwise stated, Suraksha 24X7 website content, original graphics, branding, project material and other proprietary content may not be copied, commercially reproduced or misrepresented without authorization.",
          "Third-party trademarks and product names remain the property of their respective owners.",
        ],
      },
      {
        title: "Third-party products and services",
        paragraphs: [
          "Certain systems may rely on third-party manufacturers, cloud services, mobile applications, telecom networks, software providers or internet services.",
          "Those services may have their own terms, privacy policies, subscriptions and availability conditions. Suraksha 24X7 cannot control independent third-party platforms but will provide reasonable assistance where such assistance forms part of the agreed scope.",
        ],
      },
      {
        title: "Website availability and technical access",
        paragraphs: [
          "Suraksha 24X7 aims to keep the website accessible and functioning properly; however, uninterrupted availability cannot be guaranteed at all times. Temporary interruptions may occur due to maintenance, hosting issues, internet failures, software updates, cybersecurity measures, third-party services, or other technical circumstances beyond reasonable control.",
          "Suraksha 24X7 may update, modify, suspend, or remove website features or content when reasonably required for maintenance, security, accuracy, legal compliance, or service improvement.",
        ],
      },
    ],
  },
  {
    id: "governing-law",
    title: "Governing Law & Consumer Rights",
    icon: Landmark,
    clauses: [
      {
        title: "Good-faith resolution",
        paragraphs: [
          "If a disagreement arises, both parties are encouraged to first attempt resolution through direct written communication and reasonable good-faith discussion.",
          "Nothing in this clause restricts a consumer from using any statutory grievance or consumer-dispute mechanism available under applicable law.",
        ],
      },
      {
        title: "Governing law",
        paragraphs: [
          "These Terms & Conditions are governed by the applicable laws of India.",
          "Any contractual jurisdiction provision stated in a quotation or agreement will operate only to the extent legally permissible and will not override any mandatory jurisdiction or remedy available to a consumer under applicable law.",
        ],
      },
      {
        title: "Consumer rights",
        paragraphs: [
          "Nothing contained in these Terms & Conditions is intended to waive, restrict or exclude any consumer right or remedy that cannot legally be waived or excluded under the Consumer Protection Act, 2019 or other applicable Indian law.",
          "Where any provision of these Terms conflicts with a mandatory legal requirement, the mandatory legal requirement will prevail to the extent of that conflict.",
        ],
      },
      {
        title: "Jurisdiction",
        paragraphs: [
          "Any dispute, claim, disagreement, or legal proceeding arising out of or relating to this project, quotation, purchase order, services, payment, or agreement shall be subject exclusively to the jurisdiction of the competent courts in Delhi, India.",
          "Both parties agree to submit to such jurisdiction for resolution of disputes.",
        ],
      },
    ],
  },
];

const navigation = [
  ["overview", "Overview"],
  ["about-suraksha", "About Suraksha 24X7"],
  ["customer-commitment", "Customer Commitment"],
  ["site-visit", "First Site Visit"],
  ["support", "24×7 Support"],
  ["quotation", "Quotation"],
  ["payment", "Payment"],
  ["installation", "Installation"],
  ["warranty", "Warranty"],
  ["service-amc", "Service & AMC"],
  ["cancellation", "Cancellation"],
  ["security-responsibility", "Security Responsibility"],
  ["customer-rights", "Customer Rights"],
  ["responsibility", "Liability & Force Majeure"],
  ["complaints", "Complaints"],
  ["website-terms", "Website Terms"],
  ["governing-law", "Governing Law"],
  ["service-promise", "Service Promise"],
  ["acceptance", "Acceptance of Terms"],
];

function ExpandableClauseBody({ clause }) {
  const contentRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return undefined;

    const checkOverflow = () => {
      setCanExpand(content.scrollHeight > 160);
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(content);

    return () => observer.disconnect();
  }, [clause]);

  return (
    <>
      <div
        className={`overflow-hidden transition-[max-height] duration-300 ${
          expanded
            ? "max-h-44 overflow-y-auto"
            : "max-h-40"
        }`}
      >
        <div ref={contentRef}>
          {clause.paragraphs?.map((paragraph) => (
            <p
              key={paragraph}
              className="mt-3 text-sm leading-7 text-slate-600 sm:text-[15px]"
            >
              {paragraph}
            </p>
          ))}
          {clause.bullets && (
            <ul className="mt-4 grid gap-2 text-sm leading-6 text-slate-600 sm:grid-cols-2">
              {clause.bullets.map((bullet) => (
                <li key={bullet} className="flex items-start gap-2">
                  <Check
                    size={16}
                    className="mt-1 shrink-0 text-blue-600"
                    aria-hidden="true"
                  />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {canExpand && (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="mt-3 text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700"
        >
          {expanded ? "See Less" : "See More"}
        </button>
      )}
    </>
  );
}

function ClauseCard({
  clause,
  icon: Icon,
  emphasized = false,
  className = "",
}) {
  const iconElement = createElement(Icon, {
    size: 20,
    "aria-hidden": true,
  });

  return (
    <article
      className={`h-full w-full max-w-full overflow-hidden rounded-2xl border p-5 sm:p-6 lg:h-[350px] lg:w-[462px] ${className} ${
        emphasized
          ? "border-blue-100 bg-blue-50/70"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
          {iconElement}
        </span>
        <div className="min-w-0">
          {clause.title && (
            <h3 className="text-base font-bold leading-snug text-slate-900 sm:text-lg">
              {clause.title}
            </h3>
          )}
          {(clause.paragraphs?.length > 0 || clause.bullets) && (
            <ExpandableClauseBody clause={clause} />
          )}
        </div>
      </div>
    </article>
  );
}

function TermsSectionGrid({ section, icon: Icon }) {
  const clauses = section.clauses ?? [];
  const imageOnRight = Boolean(section.imageOnRight);

  if (clauses.length >= 3) {
    return (
      <div className="grid grid-cols-1 items-stretch gap-5 md:grid-cols-2">
        {clauses.map((clause, index) => {
          const orderClass = [
            "order-1",
            "order-2",
            imageOnRight ? "order-4 md:order-3" : "order-4",
            "order-5",
            "order-6",
            "order-7",
          ][index] ?? "order-8";
          const fillsShortRow = clauses.length === 3 && index === 2;
          const balancesFinalRow =
            clauses.length > 4 &&
            index === clauses.length - 1 &&
            clauses.length % 2 === 1;

          return (
            <ClauseCard
              key={clause.title}
              clause={clause}
              icon={Icon}
              emphasized={index === 0 && Boolean(section.highlight)}
              className={`${orderClass} ${
                fillsShortRow ? "md:row-span-2" : ""
              } ${balancesFinalRow ? "md:col-span-2" : ""}`}
            />
          );
        })}

        <div
          className={`${
            imageOnRight
              ? "order-3 md:order-4"
              : "order-3 md:col-start-1 md:row-start-2"
          } flex h-55 w-full max-w-full items-center justify-center overflow-hidden rounded-2xl bg-slate-50 shadow-sm sm:h-65 md:h-full md:min-h-115 md:row-span-2 lg:w-[462px] lg:min-h-130 xl:min-h-140 2xl:min-h-150`}
        >
          <img
            src={section.image}
            alt={section.imageAlt}
            loading="lazy"
            className="block h-full w-full object-contain object-center"
          />
        </div>
      </div>
    );
  }

  /* const firstClause = clauses[0];
  const remainingClauses = clauses.slice(1);

  return (
    <div className="grid grid-cols-1 items-stretch gap-5 md:grid-cols-2">
      {firstClause && (
        <ClauseCard
          clause={firstClause}
          icon={Icon}
          emphasized={Boolean(section.highlight)}
          className={`order-1 ${imageOnRight ? "md:order-1" : "md:order-2"}`}
        />
      )}
      <div
        className={`order-2 flex h-55 w-full max-w-full items-center justify-center overflow-hidden rounded-2xl bg-slate-50 shadow-sm sm:h-60 md:h-75 lg:h-[346px] lg:w-[462px] ${
          imageOnRight ? "md:order-2" : "md:order-1"
        }`}
      >
        <img
          src={section.image}
          alt={section.imageAlt}
          loading="lazy"
          className="block h-full w-full object-contain object-center"
        />
      </div>
      {remainingClauses.map((clause) => (
        <ClauseCard
          key={clause.title}
          clause={clause}
          icon={Icon}
          className="order-3 md:col-span-2"
        />
      ))}
    </div>
  ); */

  const firstClause = clauses[0];
const secondClause = clauses[1];

return (
  <div className="grid grid-cols-1 items-stretch gap-5 md:grid-cols-2">

    {/* CARD 1 */}
    {firstClause && (
      <ClauseCard
        clause={firstClause}
        icon={Icon}
        emphasized={Boolean(section.highlight)}
        className={`
          order-1
          ${
            imageOnRight
              ? "md:col-start-1 md:row-start-1"
              : "md:col-start-2 md:row-start-1"
          }
        `}
      />
    )}

    {/* IMAGE - SPANS BOTH CARD ROWS */}
    <div
      className={`
        order-2
        flex
        w-full
        max-w-full
        items-center
        justify-center
        overflow-hidden
        rounded-2xl
        bg-slate-50
        shadow-sm

        h-[240px]
        sm:h-[280px]

        md:h-full
        md:min-h-0
        md:row-span-2

        lg:w-[462px]

        ${
          imageOnRight
            ? "md:col-start-2 md:row-start-1"
            : "md:col-start-1 md:row-start-1"
        }
      `}
    >
      <img
        src={section.image}
        alt={section.imageAlt}
        loading="lazy"
        className="
          block
          h-full
          w-full
          object-contain
          object-center
        "
      />
    </div>

    {/* CARD 2 */}
    {secondClause && (
      <ClauseCard
        clause={secondClause}
        icon={Icon}
        className={`
          order-3
          ${
            imageOnRight
              ? "md:col-start-1 md:row-start-2"
              : "md:col-start-2 md:row-start-2"
          }
        `}
      />
    )}
  </div>
);
}

function TermsSection({ section }) {
  const Icon = section.icon;

  return (
    <section
      id={section.id}
      className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8 lg:p-9"
    >
      <div className="mb-6 flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
          <Icon size={24} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
            Suraksha 24X7 • Terms & Conditions
          </p>
          <h2 className="mt-1 text-2xl font-bold leading-tight text-slate-950 sm:text-3xl">
            {section.title}
          </h2>
          {section.lead && (
            <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-base">
              {section.lead}
            </p>
          )}
        </div>
      </div>

      {section.highlight && (
        <div className="mb-6 rounded-2xl border-l-4 border-blue-600 bg-blue-50 px-5 py-4 text-base font-semibold leading-7 text-blue-950 sm:px-6 sm:text-lg">
          {section.highlight}
        </div>
      )}
      {section.callout && (
        <div className="mb-6 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-7 text-amber-950 sm:p-5">
          <ShieldCheck
            size={20}
            className="mt-1 shrink-0 text-amber-700"
            aria-hidden="true"
          />
          <p>{section.callout}</p>
        </div>
      )}
      {section.bullets && (
        <ul className="mb-6 grid gap-3 sm:grid-cols-2">
          {section.bullets.map((bullet) => (
            <li
              key={bullet}
              className="flex items-start gap-3 rounded-xl bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700"
            >
              <Check
                size={17}
                className="mt-1 shrink-0 text-emerald-600"
                aria-hidden="true"
              />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      {section.columns ? (
        <div className="grid gap-5 xl:grid-cols-2">
          {section.columns.map((column) => {
            const ColumnIcon = column.icon;
            return (
              <div
                key={column.title}
                className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5"
              >
                <div className="mb-4 flex items-center gap-3">
                  <ColumnIcon className="text-blue-700" size={22} />
                  <h3 className="text-lg font-bold text-slate-900">
                    {column.title}
                  </h3>
                </div>
                <div className="grid gap-4">
                  {column.clauses.map((clause) => (
                    <ClauseCard
                      key={clause.title}
                      clause={clause}
                      icon={ColumnIcon}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : section.image ? (
        <TermsSectionGrid section={section} icon={Icon} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {section.clauses?.map((clause) => (
            <ClauseCard key={clause.title} clause={clause} icon={Icon} />
          ))}
        </div>
      )}
    </section>
  );
}

function TermsAndConditions() {
  const [activeSection, setActiveSection] = useState(
    navigation?.[0]?.[0] || "",
  );

  useEffect(() => {
    const sections = navigation
      .map(([id]) => document.getElementById(id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      {
        root: null,
        rootMargin: "-25% 0px -60% 0px",
        threshold: [0.1, 0.25, 0.5],
      },
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-slate-50 text-slate-900">
      <section className="relative isolate bg-slate-950 text-white">
        <img
          src={imageUrls.overview2}
          alt="Terms and conditions for Suraksha 24X7"
          className="absolute inset-0 -z-20 h-full w-full object-contain object-center"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-900/55" />
        <div className="grid w-full gap-10 px-4 py-14 sm:px-6 sm:py-20 lg:grid-cols-[1fr_0.75fr] lg:items-center lg:px-10 lg:py-24 xl:px-16">
          <div>
            <nav
              aria-label="Breadcrumb"
              className="mb-8 flex flex-wrap items-center gap-2 text-sm text-blue-100"
            >
              <Link className="hover:text-white" to="/">
                Home
              </Link>
              <ChevronRight size={15} aria-hidden="true" />
              <span>Resources</span>
              <ChevronRight size={15} aria-hidden="true" />
              <span className="font-semibold text-white">
                Terms & Conditions
              </span>
            </nav>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-blue-100">
              <ShieldCheck size={16} aria-hidden="true" />
              Transparent terms • Fair service
            </span>
            <h1 className="mt-6 text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl">
              Terms & Conditions
            </h1>
            <p className="mt-4 text-xl font-semibold text-blue-100 sm:text-2xl">
              Transparent Terms. Fair Service. Long-Term Trust.
            </p>
            <p className="mt-5 max-w-2xl text-base leading-8 text-slate-200 sm:text-lg">
              Understand the terms that govern Suraksha 24X7 products,
              installations, projects, payments, warranties, service support and
              customer responsibilities.
            </p>
            <p className="mt-6 inline-flex rounded-full border border-white/15 bg-black/20 px-4 py-2 text-sm text-white">
              Effective Date: October 2026
            </p>
          </div>
          <div className="rounded-3xl border border-white/20 bg-slate-50 p-3 shadow-2xl backdrop-blur-sm">
            <img
              src={imageUrls.overview}
              alt="Suraksha 24X7 terms and conditions"
              className="aspect-[4/3] w-full rounded-2xl object-contain object-center"
            />
          </div>
        </div>
      </section>

      <div className="w-full px-4 py-10 sm:px-6 sm:py-12 lg:px-10 lg:py-16 xl:px-16">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-14 xl:grid-cols-[minmax(0,1fr)_340px]">
          <main className="order-2 min-w-0 w-full space-y-6 sm:space-y-8 lg:order-1">
            <section
              id="overview"
              className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-9"
            >
              <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
                    An overview
                  </p>
                  <h2 className="mt-2 text-2xl font-bold leading-tight text-slate-950 sm:text-3xl">
                    Clear Terms for a Transparent Customer Relationship
                  </h2>
                  <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-base">
                    Welcome to Suraksha 24X7. These Terms & Conditions govern
                    enquiries, quotations, site visits, purchases, projects,
                    installations, maintenance, technical support and other
                    services provided through or in connection with Suraksha
                    24X7.
                  </p>
                  <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-base">
                    By placing an order, accepting a quotation, making a
                    payment, requesting installation or engaging our services,
                    the customer agrees to the applicable terms stated below
                    together with the final quotation, invoice, work order or
                    service agreement.
                  </p>
                  <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-base">
                    These terms are intended to establish a transparent and
                    professional relationship in which both Suraksha 24X7 and
                    the customer understand their respective responsibilities.
                  </p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-5 sm:p-6">
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                      <FileText size={22} aria-hidden="true" />
                    </span>
                    <h3 className="text-lg font-bold text-slate-900">
                      These terms apply to
                    </h3>
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {scopeItems.map((item) => (
                      <div
                        key={item}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-center text-xs font-semibold text-slate-700 sm:text-sm"
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <div className="min-w-0 space-y-6 sm:space-y-8">
              {sections.map((section) => (
                <TermsSection key={section.id} section={section} />
              ))}

              <section
                id="service-promise"
                className="scroll-mt-28 overflow-hidden rounded-3xl bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6 text-white shadow-xl sm:p-9"
              >
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-200">
                  Our service promise
                </p>
                <h2 className="mt-2 text-2xl font-bold leading-tight sm:text-3xl">
                  Security You Can Trust. Service You Can Depend On.
                </h2>
                <p className="mt-4 max-w-3xl text-sm leading-7 text-blue-100 sm:text-base">
                  At Suraksha 24X7, our objective is not simply to supply
                  products. We aim to develop long-term customer relationships
                  through responsible consultation, authentic products,
                  professional installation, transparent commercial practices
                  and dependable after-sales support.
                </p>
                <div className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {[
                    "First eligible preliminary site visit free",
                    "Transparent quotations",
                    "Authentic and properly sourced products",
                    "Professional installation",
                    "Fast service coordination",
                    "24×7 support-request availability",
                    "Clear payment terms",
                    "Warranty assistance",
                    "Fair complaint resolution",
                    "Long-term customer support",
                  ].map((benefit) => (
                    <div
                      key={benefit}
                      className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-sm font-medium leading-6"
                    >
                      <Check
                        size={18}
                        className="mt-1 shrink-0 text-emerald-300"
                        aria-hidden="true"
                      />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-7 rounded-2xl border border-blue-200/25 bg-white/10 px-5 py-4 text-center text-base font-bold leading-7 sm:text-xl">
                  Customer Commitment + Company Responsibility = Better Security
                  &amp; Long-Term Trust
                </p>
              </section>

              <section
                id="acceptance"
                className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
              >
                <div className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                    <ClipboardCheck size={24} aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
                      Final section
                    </p>
                    <h2 className="mt-1 text-2xl font-bold text-slate-950 sm:text-3xl">
                      Acceptance of Terms
                    </h2>
                    <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-base">
                      By accepting a quotation, placing an order, making
                      payment, signing a work order, requesting installation or
                      otherwise engaging Suraksha 24X7 for products or services,
                      the customer acknowledges the applicable Terms &
                      Conditions together with the quotation, invoice, work
                      order, AMC/SLA and other expressly agreed project
                      documents.
                    </p>
                    <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-base">
                      Where a project-specific written agreement conflicts with
                      these general Terms & Conditions, the project-specific
                      agreement will apply to that matter, subject always to
                      applicable law.
                    </p>
                    <p className="mt-5 font-bold text-slate-900">
                      Suraksha 24X7
                    </p>
                    <p className="text-sm text-slate-500">
                      Security • Surveillance • IT • Smart Technology • Service
                      & Support
                    </p>
                    <p className="mt-4 text-xs text-slate-400">
                      © Suraksha 24X7. All rights reserved.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </main>

          <aside className="order-1 min-w-0 self-start lg:sticky lg:top-40 lg:order-2">
            <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
              <h3 className="border-b border-gray-300 pb-4 text-center text-lg font-bold text-gray-900">
                Terms &amp; Conditions
              </h3>
              <nav
                aria-label="Terms sections"
                className="mt-5 flex max-h-60 gap-2 overflow-x-auto pb-1 lg:block lg:max-h-[calc(100vh-18rem)] lg:overflow-y-auto lg:overflow-x-hidden"
              >
                {navigation.map(([id, label]) => {
                  const isActive = activeSection === id;

                  return (
                    <a
                      key={id}
                      href={`#${id}`}
                      onClick={(event) => {
                        const target = document.getElementById(id);

                        if (target) {
                          event.preventDefault();

                          setActiveSection(id);

                          window.history.pushState(null, "", `#${id}`);

                          target.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }
                      }}
                      className={`
        block
        shrink-0
        whitespace-nowrap
        rounded-lg
        px-3
        py-2.5
        text-sm
        font-medium
        transition-all
        duration-200
        lg:whitespace-normal

        ${
          isActive
            ? "bg-blue-600 text-white shadow-sm"
            : "bg-blue-50/40 text-gray-700 hover:translate-x-1 hover:bg-blue-100 hover:text-blue-700"
        }
      `}
                    >
                      {label}
                    </a>
                  );
                })}
              </nav>
            </div>
          </aside>
        </div>

        <section className="mt-10 rounded-3xl bg-blue-600 px-6 py-9 text-white shadow-lg sm:px-10 sm:py-12">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-2xl font-bold sm:text-3xl">
              Have Questions About Our Terms?
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-7 text-blue-50 sm:text-base">
              Our team can help you understand project scope, payment terms,
              warranty, installation and service conditions before you proceed.
            </p>
            <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-blue-700 transition hover:bg-blue-50"
              >
                Contact Us <ArrowRight size={17} aria-hidden="true" />
              </Link>
              <Link
                to="/contact?request=site-visit"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/50 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/10"
              >
                Request Site Visit <MapPin size={17} aria-hidden="true" />
              </Link>
              <a
                href="tel:+918448341881"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/50 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/10"
              >
                Call Now <Phone size={17} aria-hidden="true" />
              </a>
              <Link
                to="/complaint"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/50 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/10"
              >
                Raise a Complaint <ArrowRight size={17} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default TermsAndConditions;
